/**
 * Pure matchers for the LOCAL column filters of the pipeline board
 * (modal "Filtrar itens: <coluna>" in PipelineColumn.vue).
 *
 * Field-shape contract (serialize_card_for_index — pipeline_cards_controller.rb):
 * - `priority` lives at the card ROOT (string column, default 'none', NOT NULL).
 *   `item_details.priority` is a legacy mirror kept only as fallback.
 * - Card value follows the server-side filter semantics
 *   (PipelineCard::VALUE_NUMERIC_SQL): COALESCE(expected_revenue,
 *   item_details.value::numeric). Valueless cards never match a value
 *   filter (NULL-rejecting predicates), mirroring `with_value_between`.
 * - The assigned agent is `owner_id` at the ROOT. The legacy candidates
 *   (assigned_agent_id, assignee_id, item_details.agent_id, meta.agent_id)
 *   are kept as fallback for old payload shapes.
 *
 * Bugfix 2026-06-10: the original inline logic read item_details.priority /
 * item_details.value and never looked at owner_id, so priority/agent filters
 * matched 0 cards on current payloads (origin commit d8d04e5c18c).
 */

export const getCardPriority = card =>
  card?.priority ?? card?.item_details?.priority ?? 'none';

// JS mirror of PipelineCard::NUMERIC_VALUE_REGEXP — full-string match only.
// parseFloat would accept '123abc'/'1,5' locally while the server rejects
// them (parity break flagged by cross-review).
const NUMERIC_VALUE_REGEXP = /^[+-]?(\d{1,18}(\.\d{1,6})?|\.\d{1,6})$/;

const parseNumeric = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return NUMERIC_VALUE_REGEXP.test(trimmed) ? Number(trimmed) : null;
};

export const getCardValue = card => {
  const revenue = parseNumeric(card?.expected_revenue);
  if (revenue !== null) return revenue;

  return parseNumeric(card?.item_details?.value);
};

export const getCardAgentId = card => {
  // Root owner_id is AUTHORITATIVE when the key exists — a card unassigned
  // today (owner_id: null) must not resurrect a stale legacy agent field.
  if (card && 'owner_id' in card) {
    const id = Number(card.owner_id);
    return card.owner_id == null || Number.isNaN(id) ? null : id;
  }

  // Legacy payload shapes only (no owner_id key at all).
  const candidates = [
    card?.assigned_agent_id,
    card?.assignee_id,
    card?.item_details?.agent_id,
    card?.meta?.agent_id,
  ];
  const found = candidates.find(candidate => {
    if (candidate === null || candidate === undefined || candidate === '') {
      return false;
    }
    return Number.isFinite(Number(candidate));
  });
  return found === undefined ? null : Number(found);
};

export const matchesPriorityFilter = (card, priorities) => {
  if (!priorities || priorities.length === 0) return true;
  return priorities.includes(getCardPriority(card));
};

export const matchesValueFilter = (card, range) => {
  const min = range?.min;
  const max = range?.max;
  const hasMin = min !== undefined && min !== null;
  const hasMax = max !== undefined && max !== null;
  if (!hasMin && !hasMax) return true;

  const value = getCardValue(card);
  // Server parity: valueless cards are excluded once a value filter is set.
  if (value === null) return false;
  if (hasMin && value < min) return false;
  if (hasMax && value > max) return false;
  return true;
};

export const matchesAgentFilter = (card, agentId) => {
  // Falsy (null/undefined/0/'') = no agent filter; -1 = "unassigned" sentinel.
  if (!agentId) return true;

  const cardAgentId = getCardAgentId(card);
  if (Number(agentId) === -1) return cardAgentId === null;
  return cardAgentId === Number(agentId);
};

// Bugfix ciclo 6 (2026-06-10): new Date('YYYY-MM-DD') é parseado como UTC
// midnight pela spec; em fusos negativos (BRT = UTC-3) isso recua o início
// do range para o dia ANTERIOR local (start=01/06 incluía cards de 31/05 —
// QA mediu 32 cards em vez de 30). Data-only é interpretada no fuso LOCAL
// do browser via componentes (y, m-1, d). Strings com hora/timezone seguem
// o parse nativo.
const DATE_ONLY_REGEXP = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseLocalDate = value => {
  if (typeof value === 'string') {
    const match = value.match(DATE_ONLY_REGEXP);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const matchesDateFilter = (card, dateRange) => {
  if (!dateRange || (!dateRange.start && !dateRange.end)) return true;

  const cardDate = new Date(card?.created_at);
  if (Number.isNaN(cardDate.getTime())) return false;

  if (dateRange.start) {
    const startDate = parseLocalDate(dateRange.start);
    if (startDate) {
      startDate.setHours(0, 0, 0, 0);
      if (cardDate < startDate) return false;
    }
  }
  if (dateRange.end) {
    const endDate = parseLocalDate(dateRange.end);
    if (endDate) {
      // Data-fim inclusiva: fim do dia LOCAL.
      endDate.setHours(23, 59, 59, 999);
      if (cardDate > endDate) return false;
    }
  }
  return true;
};

export const matchesColumnFilters = (card, filters) => {
  if (!filters) return true;
  return (
    matchesPriorityFilter(card, filters.priority) &&
    matchesValueFilter(card, filters.value) &&
    matchesAgentFilter(card, filters.agent_id) &&
    matchesDateFilter(card, filters.date)
  );
};
